// Chapter 5: MPCI Claims and Loss Adjustment
// Comprehensive coverage of claims procedures and loss settlement

const chapter5 = {
  id: 5,
  title: "MPCI Claims and Loss Adjustment",
  sections: [
    {
      title: "Overview of the Claims Process",
      content: `
        <h2>Overview of the Claims Process</h2>

        <p>The claims process is how insured losses are reported, verified, and settled. Understanding this process is critical for agents who help producers navigate claims.</p>

        <div class="key-concept">
          <h3>Key Concept: Claims Process Steps</h3>
          <ol>
            <li><strong>Notice of Loss:</strong> Producer reports damage to the insurance company</li>
            <li><strong>Assignment:</strong> Company assigns an adjuster to investigate</li>
            <li><strong>Inspection:</strong> Adjuster inspects damaged crop and documents findings</li>
            <li><strong>Appraisal:</strong> Adjuster determines the extent of damage</li>
            <li><strong>Claim Form:</strong> Claim for indemnity is completed after harvest</li>
            <li><strong>Review:</strong> Company reviews claim for accuracy</li>
            <li><strong>Payment:</strong> Indemnity is paid if loss is covered</li>
          </ol>
        </div>

        <h3>Roles in the Claims Process</h3>
        <ul>
          <li><strong>Insured Producer:</strong> Reports losses, provides information, completes harvest</li>
          <li><strong>Agent:</strong> Assists producer with notices and paperwork</li>
          <li><strong>Loss Adjuster:</strong> Inspects damage and completes appraisals</li>
          <li><strong>AIP (Insurance Company):</strong> Reviews claims and issues payments</li>
          <li><strong>RMA:</strong> Provides oversight and quality control</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the basic sequence of the claims process: Notice → Inspection → Appraisal → Claim Form → Review → Payment. Each step has specific requirements.</p>
        </div>
      `
    },
    {
      title: "Notice of Loss Requirements",
      content: `
        <h2>Notice of Loss Requirements</h2>

        <p>Timely and proper notice of loss is essential. Failure to provide notice can result in claim denial.</p>

        <h3>When Notice Must Be Given</h3>
        <div class="key-concept">
          <h3>Key Concept: Notice Deadlines</h3>
          <ul>
            <li><strong>Within 72 hours</strong> of discovery of damage</li>
            <li><strong>At least 15 days before:</strong>
              <ul>
                <li>Beginning harvest on any unit</li>
                <li>Destroying or abandoning any crop</li>
                <li>Putting any acreage to another use</li>
              </ul>
            </li>
            <li><strong>Within 15 days</strong> after the end of the insurance period</li>
          </ul>
        </div>

        <h3>Contents of Notice</h3>
        <p>A proper notice of loss should include:</p>
        <ul>
          <li>Name of insured and policy number</li>
          <li>Date of loss or discovery of damage</li>
          <li>Cause of loss (peril)</li>
          <li>Location of damaged acreage</li>
          <li>Type and variety of crop</li>
          <li>Estimated percentage of damage</li>
          <li>Contact information</li>
        </ul>

        <div class="important">
          <h3>Important: Written Notice</h3>
          <p>Notice should be in writing. While initial notice may be given orally, a written notice should follow promptly. The insurance company may have specific forms for reporting losses.</p>
        </div>

        <h3>Notice for Prevented Planting</h3>
        <p>For prevented planting claims:</p>
        <ul>
          <li>Notice must be given after the final planting date</li>
          <li>Before performing any post-planting activity on the land</li>
          <li>Must describe why planting was prevented</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>72 hours is the key timeframe for notice after discovering damage. Remember the 15-day notices before harvest, destruction, or putting land to other use.</p>
        </div>
      `
    },
    {
      title: "Loss Adjuster's Role",
      content: `
        <h2>Loss Adjuster's Role</h2>

        <p>Loss adjusters are specially trained professionals who investigate claims and determine the extent of damage.</p>

        <h3>Adjuster Qualifications</h3>
        <ul>
          <li>Must complete RMA-approved training</li>
          <li>Must be licensed where required by state law</li>
          <li>Must follow the Loss Adjustment Manual (LAM) procedures</li>
          <li>Typically certified for specific crops</li>
        </ul>

        <h3>Adjuster Responsibilities</h3>
        <div class="key-concept">
          <h3>Key Concept: What Adjusters Do</h3>
          <ul>
            <li><strong>Inspect:</strong> Examine the damaged crop and field conditions</li>
            <li><strong>Appraise:</strong> Estimate potential production or loss</li>
            <li><strong>Verify:</strong> Confirm acreage, share, and other policy information</li>
            <li><strong>Document:</strong> Complete required forms and take photographs</li>
            <li><strong>Determine:</strong> Identify the cause of loss and whether it's covered</li>
            <li><strong>Calculate:</strong> Compute the preliminary loss amount</li>
          </ul>
        </div>

        <h3>Types of Adjustments</h3>

        <div class="definition">
          <h4>Preliminary Inspection</h4>
          <p>An early inspection to assess damage before harvest. Used to appraise unharvested production or authorize destruction of damaged acreage.</p>
        </div>

        <div class="definition">
          <h4>Final Inspection</h4>
          <p>The inspection after harvest is complete to finalize the claim. Production is verified and the final indemnity is calculated.</p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Adjusters must follow the Loss Adjustment Manual (LAM) procedures. They verify damage, determine cause of loss, and calculate preliminary indemnities.</p>
        </div>
      `
    },
    {
      title: "Appraisal Methods",
      content: `
        <h2>Appraisal Methods</h2>

        <p>Adjusters use various methods to estimate the amount of production that will be harvested or that was lost.</p>

        <h3>Common Appraisal Methods</h3>

        <div class="definition">
          <h4>Row Count Method</h4>
          <p>Counting plants per row and estimating ears or heads per plant. Common for corn and other row crops.</p>
        </div>

        <div class="definition">
          <h4>Sample Count Method</h4>
          <p>Taking multiple samples in the field and counting plants, heads, or pods. Results are averaged and projected.</p>
        </div>

        <div class="definition">
          <h4>Farmer Certified Production</h4>
          <p>When the producer harvests and reports production, the adjuster verifies through sales records or measurements.</p>
        </div>

        <div class="definition">
          <h4>Destruction Appraisal</h4>
          <p>When crop will be destroyed before harvest, adjuster estimates potential production that would have been harvested.</p>
        </div>

        <div class="key-concept">
          <h3>Key Concept: Appraisal Factors</h3>
          <p>When appraising production, adjusters consider:</p>
          <ul>
            <li>Plant population per acre</li>
            <li>Heads, ears, or pods per plant</li>
            <li>Seeds per head/ear/pod</li>
            <li>Estimated weight per bushel</li>
            <li>Potential yield based on growing conditions</li>
            <li>Historical yields in the area</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Corn Appraisal</h4>
          <p>Adjuster finds:</p>
          <ul>
            <li>Plants per acre: 28,000</li>
            <li>Ears per plant: 0.95</li>
            <li>Kernel rows: 16</li>
            <li>Kernels per row: 34</li>
            <li>Kernels per bushel: 90,000</li>
          </ul>
          <p><strong>Calculation:</strong></p>
          <p>28,000 × 0.95 × 16 × 34 ÷ 90,000 = <strong>160.8 bu/acre</strong></p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Understand that appraisals are estimates of potential production. Know that different methods exist for different situations (standing crop vs. harvested crop).</p>
        </div>
      `
    },
    {
      title: "Production Verification",
      content: `
        <h2>Production Verification</h2>

        <p>All production must be verified through acceptable records. This is essential for calculating accurate indemnities.</p>

        <h3>Acceptable Production Evidence</h3>
        <ul>
          <li><strong>Commercial Records:</strong>
            <ul>
              <li>Elevator tickets and settlement sheets</li>
              <li>Processor receipts</li>
              <li>Warehouse receipts</li>
            </ul>
          </li>
          <li><strong>Government Records:</strong>
            <ul>
              <li>FSA Form 578 (acreage report)</li>
              <li>CCC loan documents</li>
            </ul>
          </li>
          <li><strong>Other Evidence:</strong>
            <ul>
              <li>Measurement service records</li>
              <li>Certified bin measurements</li>
              <li>Feed records (for farm-stored production)</li>
            </ul>
          </li>
        </ul>

        <div class="important">
          <h3>Important: Farm-Stored Production</h3>
          <p>When production is stored on-farm and not sold:</p>
          <ul>
            <li>Adjuster must measure bins/storage structures</li>
            <li>Calculations follow specific measurement procedures</li>
            <li>Producer must provide access for measurement</li>
            <li>Storage must be measured before commingling with other crop years</li>
          </ul>
        </div>

        <h3>Unacceptable Evidence</h3>
        <ul>
          <li>Verbal estimates without documentation</li>
          <li>Records that cannot be verified</li>
          <li>Documents with alterations or inconsistencies</li>
          <li>Records from non-standard sources without corroboration</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Producer Record Keeping</h3>
          <p>Producers should maintain:</p>
          <ul>
            <li>All sales receipts and settlement sheets</li>
            <li>Records of production by unit</li>
            <li>Feed records if production is fed</li>
            <li>Records must be kept for at least 3 years</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Commercial sales records (elevator tickets, settlement sheets) are the primary acceptable evidence. Know what constitutes acceptable vs. unacceptable evidence.</p>
        </div>
      `
    },
    {
      title: "Indemnity Calculation",
      content: `
        <h2>Indemnity Calculation</h2>

        <p>The indemnity is the payment made to the insured when a covered loss occurs. Understanding the calculation is essential.</p>

        <h3>Basic Indemnity Formula</h3>
        <div class="key-concept">
          <h3>Key Concept: Yield Protection Indemnity</h3>
          <p><strong>Indemnity = (Guarantee - Production to Count) × Price</strong></p>
          <p>Where:</p>
          <ul>
            <li><strong>Guarantee</strong> = APH Yield × Coverage Level × Acres</li>
            <li><strong>Production to Count</strong> = Harvested + Appraised production</li>
            <li><strong>Price</strong> = Price Election × Share</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Yield Protection Indemnity</h4>
          <ul>
            <li>APH Yield: 150 bu/acre</li>
            <li>Coverage Level: 75%</li>
            <li>Acres: 500</li>
            <li>Price Election: $4.00/bu</li>
            <li>Harvested Production: 50,000 bushels</li>
            <li>Share: 100%</li>
          </ul>
          <p><strong>Guarantee = 150 × 0.75 × 500 = 56,250 bushels</strong></p>
          <p><strong>Production to Count = 50,000 bushels</strong></p>
          <p><strong>Indemnity = (56,250 - 50,000) × $4.00 = $25,000</strong></p>
        </div>

        <h3>Revenue Protection Indemnity</h3>
        <div class="key-concept">
          <h3>Key Concept: RP Indemnity Formula</h3>
          <p><strong>Indemnity = Revenue Guarantee - Actual Revenue</strong></p>
          <p>Where:</p>
          <ul>
            <li><strong>Revenue Guarantee</strong> = APH × Coverage Level × HIGHER(Projected Price, Harvest Price)</li>
            <li><strong>Actual Revenue</strong> = Production to Count × Harvest Price</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Revenue Protection Indemnity</h4>
          <ul>
            <li>APH Yield: 150 bu/acre × 500 acres = 75,000 bu</li>
            <li>Coverage Level: 75%</li>
            <li>Projected Price: $4.50</li>
            <li>Harvest Price: $4.00</li>
            <li>Harvested Production: 50,000 bu</li>
          </ul>
          <p><strong>Revenue Guarantee = 75,000 × 0.75 × $4.50 = $253,125</strong></p>
          <p>(Uses projected price because it's higher)</p>
          <p><strong>Actual Revenue = 50,000 × $4.00 = $200,000</strong></p>
          <p><strong>Indemnity = $253,125 - $200,000 = $53,125</strong></p>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know both YP and RP formulas. YP is bushels short × price. RP is guarantee revenue minus actual revenue. For RP, the guarantee uses the higher of two prices.</p>
        </div>
      `
    },
    {
      title: "Production to Count",
      content: `
        <h2>Production to Count</h2>

        <p>Production to count is the total production that is credited against the guarantee. It includes all production regardless of how it's used.</p>

        <h3>Components of Production to Count</h3>
        <ul>
          <li><strong>Harvested Production:</strong> All production that is harvested and measured</li>
          <li><strong>Appraised Production:</strong> Estimated production for unharvested areas</li>
          <li><strong>Value of Unharvested Production:</strong> When crop is not harvested due to low market value</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: All Production Counts</h3>
          <p>Production to count includes:</p>
          <ul>
            <li>Production sold commercially</li>
            <li>Production fed to livestock</li>
            <li>Production in on-farm storage</li>
            <li>Production used for seed</li>
            <li>Production given away</li>
            <li>Production lost due to uninsured causes</li>
          </ul>
        </div>

        <h3>Adjustments to Production</h3>

        <div class="definition">
          <h4>Quality Adjustment</h4>
          <p>When production has quality defects, the production may be adjusted to reflect reduced value. Quality adjustment factors are established by RMA.</p>
        </div>

        <div class="definition">
          <h4>Moisture Adjustment</h4>
          <p>Production is typically adjusted to a standard moisture level. For example, corn is adjusted to 15% moisture.</p>
        </div>

        <div class="example">
          <h4>Example: Moisture Adjustment</h4>
          <p>Corn harvested at 20% moisture:</p>
          <ul>
            <li>Harvested bushels: 10,000</li>
            <li>Moisture: 20%</li>
            <li>Standard moisture: 15%</li>
            <li>Adjustment: 10,000 × (100-20)/(100-15) = 10,000 × 0.941 = 9,410 adjusted bushels</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>ALL production counts toward the guarantee - even production that's fed, stored, or given away. Production may be adjusted for moisture and quality.</p>
        </div>
      `
    },
    {
      title: "Claim Form Completion",
      content: `
        <h2>Claim Form Completion</h2>

        <p>The claim for indemnity is the formal request for payment. It must be completed accurately and submitted timely.</p>

        <h3>When to File the Claim</h3>
        <ul>
          <li>After harvest is complete</li>
          <li>Within 60 days after the earlier of:
            <ul>
              <li>End of insurance period</li>
              <li>Final adjustment of the loss</li>
            </ul>
          </li>
        </ul>

        <h3>Claim Form Contents</h3>
        <div class="key-concept">
          <h3>Key Concept: Claim Information Required</h3>
          <ul>
            <li>Policy identification information</li>
            <li>Acreage and production by unit</li>
            <li>Cause of loss certification</li>
            <li>Production records and documentation</li>
            <li>Signature certifying accuracy of information</li>
          </ul>
        </div>

        <h3>Producer Certification</h3>
        <div class="important">
          <h3>Important: Certification Statement</h3>
          <p>By signing the claim, the producer certifies that:</p>
          <ul>
            <li>All information is true and accurate</li>
            <li>All production has been reported</li>
            <li>Good farming practices were followed</li>
            <li>Loss was due to an insured cause</li>
          </ul>
          <p>False statements can result in claim denial, policy voidance, and legal penalties.</p>
        </div>

        <h3>Common Claim Issues</h3>
        <ul>
          <li>Incomplete production records</li>
          <li>Discrepancies between reported and verified acreage</li>
          <li>Production not properly documented by unit</li>
          <li>Missing signatures or certifications</li>
          <li>Late filing</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>The claim must be filed within 60 days of the earlier of end of insurance period or final adjustment. Certification carries legal consequences for false statements.</p>
        </div>
      `
    },
    {
      title: "Prevented Planting Claims",
      content: `
        <h2>Prevented Planting Claims</h2>

        <p>Prevented planting claims have unique procedures because there is no crop to appraise - the claim is that planting was impossible.</p>

        <h3>Requirements for Prevented Planting</h3>
        <ul>
          <li>Planting was prevented by an insured cause of loss</li>
          <li>Producer intended to plant the crop</li>
          <li>Producer had the inputs (seed, equipment) to plant</li>
          <li>Planting was prevented on or before the final planting date or during late planting period</li>
          <li>Acreage has been reported</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Prevented Planting Payment</h3>
          <p>The payment is a percentage of the guarantee:</p>
          <ul>
            <li><strong>60%</strong> of guarantee for corn, grain sorghum, soybeans, sunflowers</li>
            <li><strong>55%</strong> of guarantee for other crops</li>
          </ul>
          <p>These percentages may be increased with buy-up coverage.</p>
        </div>

        <h3>Documentation Required</h3>
        <ul>
          <li>Evidence of planting intention (seed purchase, equipment rental)</li>
          <li>Evidence of preventing cause (weather records, photos)</li>
          <li>Acreage report showing intended acres</li>
        </ul>

        <h3>Second Crop Considerations</h3>
        <div class="important">
          <h3>Important: Planting a Second Crop</h3>
          <p>If producer plants a second crop on prevented planting acreage:</p>
          <ul>
            <li>Payment is reduced by 35 percentage points</li>
            <li>60% becomes 25%</li>
            <li>55% becomes 20%</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Prevented planting pays 55% or 60% of guarantee. Planting a second crop reduces payment by 35 percentage points. Know the documentation requirements.</p>
        </div>
      `
    },
    {
      title: "Late Planting Claims",
      content: `
        <h2>Late Planting Claims</h2>

        <p>When a crop is planted after the final planting date, the guarantee is reduced. Understanding how this affects claims is important.</p>

        <h3>Late Planting Guarantee Reduction</h3>
        <div class="key-concept">
          <h3>Key Concept: Late Planting Reduction</h3>
          <p>For each day planted after the final planting date:</p>
          <ul>
            <li>Guarantee reduced by <strong>1% per day</strong></li>
            <li>Maximum reduction of <strong>25%</strong> during late planting period</li>
            <li>Late planting period is typically 25 days</li>
          </ul>
        </div>

        <div class="example">
          <h4>Example: Late Planting Impact on Claim</h4>
          <ul>
            <li>Original Guarantee: 120 bu/acre</li>
            <li>Planted 15 days late</li>
            <li>Late Reduction: 15%</li>
            <li>Reduced Guarantee: 120 × 0.85 = 102 bu/acre</li>
            <li>Actual Production: 90 bu/acre</li>
          </ul>
          <p><strong>Loss = 102 - 90 = 12 bu/acre</strong></p>
          <p>(Instead of 120 - 90 = 30 bu/acre with on-time planting)</p>
        </div>

        <h3>Documentation</h3>
        <ul>
          <li>Adjuster verifies actual planting date</li>
          <li>Reduction is applied automatically to guarantee</li>
          <li>Producer should maintain planting records</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Late planting reduces the guarantee by 1% per day. This affects the claim calculation - a smaller guarantee means a smaller potential indemnity.</p>
        </div>
      `
    },
    {
      title: "Replanting Claims",
      content: `
        <h2>Replanting Claims</h2>

        <p>When a crop is damaged and must be replanted, a replant payment may be available to offset the additional costs.</p>

        <h3>Replant Payment Eligibility</h3>
        <ul>
          <li>Damage must be from an insured cause of loss</li>
          <li>Damage must warrant replanting (significant stand loss)</li>
          <li>Must be practical to replant</li>
          <li>Replanting must occur</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Replant Payment</h3>
          <p>The replant payment is:</p>
          <ul>
            <li>A fixed dollar amount per acre</li>
            <li>Based on the lesser of actual costs or maximum allowed</li>
            <li>Limited to one replant payment per unit per crop year</li>
            <li>Separate from any indemnity payment</li>
          </ul>
        </div>

        <h3>Replant Payment Calculation</h3>
        <div class="example">
          <h4>Example: Replant Payment</h4>
          <ul>
            <li>Replanted acres: 100</li>
            <li>Replant payment rate: $35/acre</li>
            <li>Total replant payment: $3,500</li>
          </ul>
          <p>This is paid regardless of whether an indemnity is later paid for the replanted crop.</p>
        </div>

        <h3>Effect on Coverage</h3>
        <ul>
          <li>Original guarantee usually remains in effect if replanted timely</li>
          <li>If replanted during late planting period, reduced guarantee applies</li>
          <li>Adjuster will appraise both destroyed and replanted crop</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Replant payments are SEPARATE from indemnity payments. A producer can receive a replant payment AND an indemnity if the replanted crop also suffers a loss.</p>
        </div>
      `
    },
    {
      title: "Quality Losses",
      content: `
        <h2>Quality Losses</h2>

        <p>When production has quality defects, the production to count may be adjusted to reflect the reduced value.</p>

        <h3>When Quality Adjustment Applies</h3>
        <ul>
          <li>Production is damaged due to insured cause</li>
          <li>Quality defects reduce the value below minimum standards</li>
          <li>Defects are verified through testing or inspection</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Quality Adjustment Factor</h3>
          <p>Production is adjusted by the ratio of deficient value to full value:</p>
          <p><strong>Adjusted Production = Actual Production × (Deficient Value ÷ Full Value)</strong></p>
          <p>This reflects that damaged production has less value per bushel.</p>
        </div>

        <div class="example">
          <h4>Example: Quality Adjustment</h4>
          <ul>
            <li>Harvested production: 10,000 bushels</li>
            <li>Full market price: $4.00/bu</li>
            <li>Price for damaged corn: $3.00/bu (due to test weight)</li>
            <li>Quality Factor: $3.00 ÷ $4.00 = 0.75</li>
          </ul>
          <p><strong>Production to Count = 10,000 × 0.75 = 7,500 bushels</strong></p>
          <p>The reduced production to count increases the potential indemnity.</p>
        </div>

        <h3>Common Quality Defects</h3>
        <ul>
          <li><strong>Test Weight:</strong> Grain that weighs less than standard</li>
          <li><strong>Damage:</strong> Kernel damage from insects, disease, or weather</li>
          <li><strong>Foreign Material:</strong> Excessive debris in grain</li>
          <li><strong>Moisture:</strong> Grain above or below acceptable levels</li>
          <li><strong>Aflatoxin:</strong> Contamination from fungus</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Quality adjustment reduces the production to count, which increases the loss. The adjustment is based on the ratio of deficient price to full price.</p>
        </div>
      `
    },
    {
      title: "Appeals and Dispute Resolution",
      content: `
        <h2>Appeals and Dispute Resolution</h2>

        <p>If a producer disagrees with a claim determination, there are procedures for appeal and resolution.</p>

        <h3>Informal Review</h3>
        <p>The first step is typically an informal discussion with:</p>
        <ul>
          <li>The loss adjuster</li>
          <li>The agent</li>
          <li>The insurance company</li>
        </ul>
        <p>Many issues can be resolved through clarification or additional documentation.</p>

        <h3>Formal Appeal Process</h3>
        <div class="key-concept">
          <h3>Key Concept: Appeal Steps</h3>
          <ol>
            <li><strong>AIP Reconsideration:</strong> Request the insurance company to review the decision</li>
            <li><strong>Mediation:</strong> Voluntary process with neutral mediator</li>
            <li><strong>Arbitration:</strong> Binding decision by neutral arbitrator</li>
            <li><strong>NAD Appeal:</strong> USDA National Appeals Division (for certain disputes)</li>
            <li><strong>Litigation:</strong> Court action (limited availability)</li>
          </ol>
        </div>

        <h3>Time Limits</h3>
        <ul>
          <li>Appeals must be filed within specified time periods</li>
          <li>Typically 30-60 days from decision notification</li>
          <li>Missing deadlines may forfeit appeal rights</li>
        </ul>

        <div class="important">
          <h3>Important: Arbitration</h3>
          <p>Most policy disputes must be resolved through arbitration, not court litigation. The policy contains a binding arbitration clause.</p>
        </div>

        <h3>Common Dispute Issues</h3>
        <ul>
          <li>Cause of loss determination</li>
          <li>Production verification</li>
          <li>Acreage discrepancies</li>
          <li>Good farming practice disputes</li>
          <li>Appraisal methodology</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know that arbitration is typically required for policy disputes. The appeals process starts with informal review, then formal steps including NAD appeal.</p>
        </div>
      `
    },
    {
      title: "Fraud and Program Integrity",
      content: `
        <h2>Fraud and Program Integrity</h2>

        <p>Crop insurance fraud is taken seriously with significant consequences. Understanding fraud prevention is important for agents.</p>

        <h3>Types of Fraud</h3>
        <ul>
          <li><strong>Production Fraud:</strong> Hiding production, false yield reporting</li>
          <li><strong>Acreage Fraud:</strong> Misreporting planted or insured acreage</li>
          <li><strong>APH Manipulation:</strong> False production history records</li>
          <li><strong>False Claims:</strong> Claiming losses that didn't occur</li>
          <li><strong>Cause of Loss Misrepresentation:</strong> Claiming covered cause when actual cause wasn't covered</li>
        </ul>

        <div class="important">
          <h3>Important: Consequences of Fraud</h3>
          <ul>
            <li>Claim denial and repayment of benefits</li>
            <li>Policy voidance (cancellation as if never existed)</li>
            <li>Program disqualification (ineligibility for future coverage)</li>
            <li>Civil fines and penalties</li>
            <li>Criminal prosecution</li>
          </ul>
        </div>

        <h3>Good Farming Practice Violations</h3>
        <div class="definition">
          <p>Claims can be denied if the producer didn't follow <strong>Good Farming Practices (GFP)</strong>. Examples of GFP violations:</p>
          <ul>
            <li>Planting at improper times</li>
            <li>Failure to control weeds or pests</li>
            <li>Inadequate fertilization</li>
            <li>Failure to protect crop after damage</li>
            <li>Untimely harvest</li>
          </ul>
        </div>

        <h3>Agent Responsibilities</h3>
        <ul>
          <li>Report suspected fraud to the company</li>
          <li>Never assist in or ignore fraudulent activities</li>
          <li>Maintain accurate records</li>
          <li>Agents can also face penalties for participating in fraud</li>
        </ul>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Fraud has serious consequences including criminal prosecution. Agents have a responsibility to report suspected fraud. Claims can be denied for GFP violations.</p>
        </div>
      `
    },
    {
      title: "Common Causes of Claim Denial",
      content: `
        <h2>Common Causes of Claim Denial</h2>

        <p>Understanding why claims are denied helps agents assist producers in avoiding these issues.</p>

        <h3>Procedural Denials</h3>
        <ul>
          <li><strong>Late Notice:</strong> Failure to report loss within 72 hours</li>
          <li><strong>Late Claim:</strong> Not filing claim within 60 days</li>
          <li><strong>Missing Documentation:</strong> Inability to verify production</li>
          <li><strong>Unauthorized Actions:</strong> Destroying crop without consent</li>
        </ul>

        <h3>Coverage Denials</h3>
        <ul>
          <li><strong>Uninsured Cause:</strong> Loss was not from a covered peril</li>
          <li><strong>Pre-existing Condition:</strong> Damage occurred before coverage began</li>
          <li><strong>Policy Not in Force:</strong> Premium not paid, policy cancelled</li>
          <li><strong>Acreage Not Reported:</strong> Damaged acres weren't on the acreage report</li>
        </ul>

        <h3>Practice Denials</h3>
        <ul>
          <li><strong>Poor Farming Practice:</strong> Loss due to negligence or mismanagement</li>
          <li><strong>Avoidable Loss:</strong> Producer could have prevented the damage</li>
          <li><strong>Failure to Protect:</strong> Not taking steps to minimize loss</li>
        </ul>

        <div class="key-concept">
          <h3>Key Concept: Avoiding Denials</h3>
          <p>Agents should advise producers to:</p>
          <ul>
            <li>Report losses promptly</li>
            <li>Maintain complete records</li>
            <li>Follow good farming practices</li>
            <li>Never destroy crop without authorization</li>
            <li>File claims timely with complete documentation</li>
          </ul>
        </div>

        <div class="exam-tip">
          <h3>Exam Tip</h3>
          <p>Know the common reasons for claim denial: late notice, uninsured cause, poor farming practices, and missing documentation.</p>
        </div>
      `
    }
  ],
  quizQuestions: [
    {
      question: "Notice of loss must be provided within how many hours of discovering damage?",
      options: [
        "24 hours",
        "48 hours",
        "72 hours",
        "96 hours"
      ],
      correct: 2,
      explanation: "Notice of loss must be provided within 72 hours of initial discovery of damage. This allows the insurance company to inspect damage while evidence is still available."
    },
    {
      question: "Before destroying a damaged crop, the producer must:",
      options: [
        "Wait 15 days after the loss",
        "Notify the company at least 15 days before and receive written consent",
        "File a claim form",
        "Pay the deductible"
      ],
      correct: 1,
      explanation: "The producer must give at least 15 days' notice before destroying or putting any acreage to another use, and must receive written consent from the company."
    },
    {
      question: "A loss adjuster's primary responsibility is to:",
      options: [
        "Sell additional coverage to the producer",
        "Inspect damage and determine the extent of loss",
        "Process premium payments",
        "File claims on behalf of the producer"
      ],
      correct: 1,
      explanation: "Loss adjusters inspect the damaged crop, document findings, appraise production, and determine the extent of loss following Loss Adjustment Manual procedures."
    },
    {
      question: "Production to count includes all of the following EXCEPT:",
      options: [
        "Production sold commercially",
        "Production fed to livestock",
        "Production lost to uninsured causes",
        "Production never planted"
      ],
      correct: 3,
      explanation: "Production to count includes all production - sold, stored, fed, or lost to uninsured causes. Unplanted production cannot be counted as it never existed."
    },
    {
      question: "The basic formula for a Yield Protection indemnity is:",
      options: [
        "Actual Production × Price",
        "(Guarantee - Production to Count) × Price",
        "Guarantee × Coverage Level",
        "APH Yield - Actual Yield"
      ],
      correct: 1,
      explanation: "The YP indemnity formula is (Guarantee - Production to Count) × Price. This calculates the bushels short times the price election."
    },
    {
      question: "Quality adjustment to production is based on:",
      options: [
        "The producer's opinion of value",
        "The ratio of deficient price to full market price",
        "A fixed percentage regardless of damage",
        "The insurance company's preference"
      ],
      correct: 1,
      explanation: "Quality adjustment uses the ratio of the deficient (damaged) price to the full market price. Adjusted Production = Actual × (Deficient Price ÷ Full Price)."
    },
    {
      question: "A claim for indemnity must be filed within how many days of the end of insurance period?",
      options: [
        "30 days",
        "45 days",
        "60 days",
        "90 days"
      ],
      correct: 2,
      explanation: "The claim must be filed within 60 days after the earlier of the end of the insurance period or the final adjustment of loss."
    },
    {
      question: "The prevented planting payment for corn is typically what percentage of the guarantee?",
      options: [
        "45%",
        "55%",
        "60%",
        "75%"
      ],
      correct: 2,
      explanation: "Corn has a 60% prevented planting payment. Some crops receive 55%. These can be increased with additional premium for higher prevented planting coverage."
    },
    {
      question: "If a producer plants a second crop on prevented planting acreage, the payment is:",
      options: [
        "Eliminated entirely",
        "Reduced by 35 percentage points",
        "Unchanged",
        "Increased by 15 percentage points"
      ],
      correct: 1,
      explanation: "Planting a second crop on prevented planting acres reduces the payment by 35 percentage points. A 60% payment becomes 25%; a 55% payment becomes 20%."
    },
    {
      question: "For late planting, the guarantee is reduced by:",
      options: [
        "1% per day up to 25%",
        "2% per day up to 50%",
        "5% per week up to 25%",
        "A flat 10% regardless of days late"
      ],
      correct: 0,
      explanation: "The guarantee is reduced by 1% for each day planted after the final planting date, up to a maximum reduction of 25% during the late planting period."
    },
    {
      question: "A replant payment is:",
      options: [
        "Deducted from any indemnity payment",
        "Separate from and in addition to any indemnity",
        "Only paid if no indemnity is due",
        "Part of the premium calculation"
      ],
      correct: 1,
      explanation: "Replant payments are separate from indemnity payments. A producer can receive both a replant payment and an indemnity if the replanted crop also suffers a covered loss."
    },
    {
      question: "Which is acceptable evidence of production?",
      options: [
        "Producer's verbal estimate",
        "Elevator settlement sheets",
        "Neighbor's testimony",
        "Unverified personal records"
      ],
      correct: 1,
      explanation: "Commercial sales records like elevator tickets and settlement sheets are acceptable evidence. Verbal estimates and unverified records are not acceptable."
    },
    {
      question: "Most crop insurance policy disputes must be resolved through:",
      options: [
        "State court litigation",
        "Federal court litigation",
        "Binding arbitration",
        "Informal negotiation only"
      ],
      correct: 2,
      explanation: "Crop insurance policies contain binding arbitration clauses. Most disputes must be resolved through arbitration rather than court litigation."
    },
    {
      question: "Which is a consequence of crop insurance fraud?",
      options: [
        "Warning letter only",
        "Premium discount",
        "Criminal prosecution",
        "Extended coverage period"
      ],
      correct: 2,
      explanation: "Fraud can result in claim denial, policy voidance, civil penalties, program disqualification, and criminal prosecution. It is taken very seriously."
    },
    {
      question: "A claim may be denied for poor farming practices if:",
      options: [
        "The producer used a different seed variety",
        "The loss could have been avoided with proper management",
        "The producer's yields are below county average",
        "The producer has filed previous claims"
      ],
      correct: 1,
      explanation: "Claims can be denied if losses were due to poor farming practices - the loss could have been prevented or reduced through proper crop management."
    },
    {
      question: "The moisture adjustment for corn converts production to what standard moisture level?",
      options: [
        "12%",
        "13%",
        "15%",
        "18%"
      ],
      correct: 2,
      explanation: "Corn production is adjusted to 15% moisture, which is the standard. Higher moisture corn is adjusted down to reflect the actual dry matter content."
    },
    {
      question: "What role does the agent play in the claims process?",
      options: [
        "Conducting loss adjustments",
        "Approving or denying claims",
        "Assisting producers with notices and paperwork",
        "Setting indemnity payment amounts"
      ],
      correct: 2,
      explanation: "Agents assist producers with reporting losses, completing paperwork, and navigating the claims process. Adjusters investigate claims; companies approve payments."
    },
    {
      question: "A preliminary inspection is conducted:",
      options: [
        "Before the policy is issued",
        "Before harvest to assess damage",
        "Only after the claim is paid",
        "At the sales closing date"
      ],
      correct: 1,
      explanation: "A preliminary inspection is an early inspection before harvest to assess damage. It may be used to appraise standing production or authorize crop destruction."
    },
    {
      question: "For Revenue Protection, actual revenue is calculated as:",
      options: [
        "Production × Projected Price",
        "Production to Count × Harvest Price",
        "APH × Coverage Level × Price",
        "Guarantee minus deductible"
      ],
      correct: 1,
      explanation: "Actual revenue equals Production to Count times the Harvest Price. This is compared to the revenue guarantee to determine if an indemnity is due."
    },
    {
      question: "When farm-stored production must be verified:",
      options: [
        "The producer provides a verbal estimate",
        "The adjuster measures the storage structure",
        "Only commercial sales records are used",
        "No verification is needed"
      ],
      correct: 1,
      explanation: "When production is stored on-farm, the adjuster must measure the bins or storage structures using specific measurement procedures. Verbal estimates are not acceptable."
    },
    {
      question: "The Loss Adjustment Manual (LAM) provides:",
      options: [
        "Premium calculation methods",
        "Standard procedures for adjusters to follow",
        "Marketing guidelines for agents",
        "Policy application forms"
      ],
      correct: 1,
      explanation: "The LAM provides standard procedures that adjusters must follow when inspecting damage, appraising production, and calculating losses."
    },
    {
      question: "A producer's signature on the claim form certifies:",
      options: [
        "Only that they received the claim form",
        "That all information is accurate and complete",
        "That they agree with the company's decision",
        "Only that harvest is complete"
      ],
      correct: 1,
      explanation: "By signing, the producer certifies that all information is accurate, production is fully reported, good farming practices were followed, and loss was from an insured cause."
    },
    {
      question: "If a producer fails to provide timely notice of loss:",
      options: [
        "The premium is waived",
        "The claim may be denied",
        "Additional coverage is provided",
        "The deductible is reduced"
      ],
      correct: 1,
      explanation: "Failure to provide timely notice (within 72 hours) can result in claim denial. Timely notice is essential to allow inspection while evidence of damage exists."
    },
    {
      question: "An appraisal of unharvested production is used to:",
      options: [
        "Determine the premium amount",
        "Estimate production for areas that won't be harvested",
        "Calculate next year's APH yield",
        "Set the projected price"
      ],
      correct: 1,
      explanation: "Appraisals estimate the production that would have been harvested from unharvested areas. This is counted toward production and affects the indemnity calculation."
    },
    {
      question: "Documentation for prevented planting should include:",
      options: [
        "Only the acreage report",
        "Evidence of planting intention and preventing cause",
        "The claim form only",
        "No documentation is required"
      ],
      correct: 1,
      explanation: "Prevented planting claims require evidence of planting intention (seed purchase, equipment rental) and evidence of the preventing cause (weather records, photos)."
    },
    {
      question: "What is the purpose of a final adjustment?",
      options: [
        "To calculate the premium",
        "To determine the final indemnity after all production is accounted for",
        "To set the APH for next year",
        "To cancel the policy"
      ],
      correct: 1,
      explanation: "The final adjustment occurs after harvest to determine the actual indemnity payment by accounting for all production - harvested, appraised, and any quality adjustments."
    },
    {
      question: "Production from uninsured acreage is:",
      options: [
        "Counted toward the unit's production",
        "Ignored in the claim calculation",
        "Added to the guarantee",
        "Paid at a reduced rate"
      ],
      correct: 0,
      explanation: "Production from uninsured acreage in the same unit must be counted toward total production. You cannot exclude high-yielding uninsured acres to inflate a claim."
    },
    {
      question: "A crop that cannot be harvested with normal equipment due to damage may be:",
      options: [
        "Automatically covered at 100%",
        "Appraised for unharvested production",
        "Ignored in the claim",
        "Covered only if replanted"
      ],
      correct: 1,
      explanation: "When crops cannot be harvested normally due to damage, adjusters appraise the unharvested production to estimate what would have been harvested, which is counted in the claim."
    },
    {
      question: "What happens if a producer fails to maintain records as required?",
      options: [
        "Nothing, records are optional",
        "Premium is reduced",
        "Claims may be denied or coverage reduced",
        "Coverage is automatically increased"
      ],
      correct: 2,
      explanation: "Failure to maintain required records can result in claim denial, use of assigned yields, or reduced coverage. Records are essential to verify production and support claims."
    },
    {
      question: "The RP indemnity formula considers:",
      options: [
        "Only yield shortfall",
        "Only price decline",
        "Both yield and price - revenue shortfall below guarantee",
        "Neither yield nor price"
      ],
      correct: 2,
      explanation: "Revenue Protection pays when actual revenue (production × harvest price) falls below the revenue guarantee, considering both yield losses and price changes."
    }
  ]
};

export default chapter5;
